import moment from 'moment'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import arrowRightIcon from '@assets/images/arrow-right-icon.svg'
import localizationIcon from '@assets/images/observation-localization-icon.svg'

import PenIcon from '@assets/images/pen-icon.svg?react'
import BuildingIcon from '@assets/images/task-building-icon.svg?react'
import CalendarIcon from '@assets/images/task-calendar-icon.svg?react'
import TimerIcon from '@assets/images/task-duration-icon.svg?react'
import FloorIcon from '@assets/images/task-floor-icon.svg?react'
import PeopleIcon from '@assets/images/task-people-icon.svg?react'
import ZoneIcon from '@assets/images/task-zone-icon.svg?react'

import { CommentableType, RemoteFile, Task, TaskStatus, Zone } from '@types'

import { useFilePreviews } from '@hooks/file/file-preview.hook'

import { Refresh } from '@components/Refresh'
import { Slider } from '@components/Slider'

import { SelectTaskStatus } from '@modules/_shared/components/SelectTaskStatus'
import { TimelineActivities } from '@modules/_shared/components/TimelineActivities'
import { PdfMarkerPreview } from '@modules/_shared/components/PdfMarkerPreview'

import { mapToMarker } from '@libs/apryse/utils'

import { useDebounceCallback } from '@utils/hooks'

import {
  ArrowRightIcon,
  IconImg,
  LocalizationContainer,
  ProgressContainer,
  ProgressPercentage,
  RowContainer,
  SectionActions,
  SectionContainer,
  SectionTitle,
  TextButtonLocalization,
  ValueContainer,
  ValueLabel,
} from './TaskDetails.styled'

interface TaskDetailsProps {
  task: Task
  building?: Zone
  floor?: Zone
  zone?: Zone
  canEditTaskStatus: boolean
  canEditTask: boolean
  loading: 0 | 1 | -1
  onStatusChange: (status?: TaskStatus) => void
  onProgressChange: (progress: number) => void
  onEditClick: VoidFunction
  onRefresh: VoidFunction
  onLocalizationClick: VoidFunction
}

export const TaskDetails: React.FC<TaskDetailsProps> = ({
  task,
  building,
  floor,
  zone,
  canEditTaskStatus,
  canEditTask,
  loading,
  onStatusChange,
  onProgressChange,
  onEditClick,
  onRefresh,
  onLocalizationClick,
}) => {
  const { t } = useTranslation()

  const [plan, setPlan] = useState<RemoteFile>()

  const progressRef = useRef<number>(task.progress)
  const [progressDisplay, setProgressDisplay] = useState<number>(task.progress)

  const debouncedUpdateProgress = useDebounceCallback((progress) => {
    onProgressChange(progress)
  }, 500)

  const { loadFilePreviews: loadPlanPreview } = useFilePreviews({
    files: task?.projectFile?.file ? ([task?.projectFile?.file] as RemoteFile[]) : undefined,
    onFilesLoaded: ([file]) => setPlan(file as RemoteFile),
  })

  useEffect(() => {
    if (!task?.projectFile?.file) return

    loadPlanPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.projectFile?.file])

  const handleProgressChange = useCallback(
    (value: number) => {
      progressRef.current = value

      setProgressDisplay(value)

      debouncedUpdateProgress(value)
    },
    [debouncedUpdateProgress],
  )

  return (
    <Refresh small loading={loading} onRefresh={onRefresh}>
      <RowContainer>
        <SectionContainer>
          <SectionTitle>{t('modules_project-suivi_components_TaskDetails_zoning_label')}</SectionTitle>
          {building && (
            <ValueContainer>
              <IconImg>
                <BuildingIcon />
              </IconImg>
              <ValueLabel>{building.name}</ValueLabel>
            </ValueContainer>
          )}
          {floor && (
            <ValueContainer>
              <IconImg>
                <FloorIcon />
              </IconImg>
              <ValueLabel>{floor.name}</ValueLabel>
            </ValueContainer>
          )}
          {zone && (
            <ValueContainer>
              <IconImg>
                <ZoneIcon />
              </IconImg>
              <ValueLabel>{zone.name}</ValueLabel>
            </ValueContainer>
          )}
        </SectionContainer>
        <SectionContainer>
          <SectionTitle>{t('modules_project-suivi_components_TaskDetails_details_label')}</SectionTitle>
          <ValueContainer>
            <IconImg>
              <PeopleIcon />
            </IconImg>
            <ValueLabel>
              {t('modules_project-suivi_components_TaskDetails_of_people', {
                count: task.workforce,
              })}
            </ValueLabel>
          </ValueContainer>
          <ValueContainer>
            <IconImg>
              <TimerIcon />
            </IconImg>
            <ValueLabel>
              {t('modules_project-suivi_components_TaskDetails_of_days', {
                count: task.requiredDuration,
              })}
            </ValueLabel>
          </ValueContainer>
          <ValueContainer>
            <IconImg>
              <CalendarIcon />
            </IconImg>
            <ValueLabel>{moment(task.startAt).format('L')}</ValueLabel>
          </ValueContainer>
        </SectionContainer>
        {canEditTask && (
          <SectionActions>
            <div onClick={onEditClick}>
              <PenIcon />
            </div>
          </SectionActions>
        )}
      </RowContainer>

      {plan && task.projectFilePositions && task.projectFilePositions.length > 0 && (
        <RowContainer>
          <SectionContainer>
            <SectionTitle>{t('modules_project-suivi_components_TaskDetails_localization_label')}</SectionTitle>
            <LocalizationContainer onClick={canEditTask ? onLocalizationClick : undefined}>
              {plan?.url && task.projectFile && (
                <>
                  <PdfMarkerPreview pdfUrl={plan.url} initialMarker={mapToMarker(task, 'task')} />
                  {canEditTask && <ArrowRightIcon src={arrowRightIcon} />}
                </>
              )}
            </LocalizationContainer>
          </SectionContainer>
        </RowContainer>
      )}

      {canEditTask && !task.projectFilePositions && onLocalizationClick && (
        <>
          <RowContainer>
            <SectionContainer>
              <SectionTitle>{t('modules_project-suivi_components_TaskDetails_localization_label')}</SectionTitle>
              <TextButtonLocalization type="button" onClick={onLocalizationClick}>
                <img src={localizationIcon} />
                {t('modules_project-suivi_components_TaskDetails_localization_button_label')}
              </TextButtonLocalization>
            </SectionContainer>
          </RowContainer>
        </>
      )}

      <RowContainer>
        <SectionContainer style={{ paddingBottom: task.status === TaskStatus.StatusInProgress ? 12 : 24 }}>
          <SectionTitle>{t('modules_project-suivi_components_TaskDetails_status_label')}</SectionTitle>
          <SelectTaskStatus
            required
            disabled={!canEditTaskStatus}
            status={task.status}
            onSelectTaskStatus={onStatusChange}
          />
        </SectionContainer>
      </RowContainer>

      {task.status === TaskStatus.StatusInProgress && (
        <ProgressContainer style={{ paddingBottom: 24 }}>
          <Slider progress={task.progress} onChange={handleProgressChange} disabled={!canEditTask} />
          <ProgressPercentage>{progressDisplay}%</ProgressPercentage>
        </ProgressContainer>
      )}

      <RowContainer>
        <SectionContainer style={{ paddingBottom: 24 }}>
          <SectionTitle>{t('modules_project-suivi_components_TaskDetails_activity_label')}</SectionTitle>
          <TimelineActivities activities={task.activities} comments={task.comments} type={CommentableType.Task} />
        </SectionContainer>
      </RowContainer>
    </Refresh>
  )
}
